import {
    DepartmentCategory,
    DepartmentVisibility,
    ParentSectionType,
} from "./Event";
import { SimpleUser } from "./User";

export enum SeatStatus {
    UNAVAILABLE = 3,
    RESERVED = 4,
    AVAILABLE = 2,
}

export interface EventLayout {
    logoImg: string;
    rightText: string;
    background: EventLayoutBackground;
    sections: Array<EventLayoutSection>;
    generalSections: Array<EventLayoutGeneralSection>;
    shapes: Array<EventLayoutShape>;
    texts: Array<EventLayoutText>;
    images: Array<EventLayoutImage>;
    pansZoomMode: "SECTION_TOGGLE" | "MOUSE_WHEEL";
}

export interface EventStatus {
    eventId: number;
    maxItemsInCart: number;
    departments: Array<Department>;
    permissions: PermissionSummary;
    seats: Array<SeatDetails>;
    overrides: Array<any>; // Not currently supported
    relationships: Array<EventRelationship>; // Not currently supported
    relatedEvents: Array<any>; // Not currently supported
}

export interface EventRelationship {
    relationship: string;
    sourceTicketSummaryId: number;
    targetTicketSummaryId: number;
    targetName: string;
    targetPrice: number; // Decimal price (ex. 19.99)
}

export interface EventLayoutBackground {
    svg?: string;
    svgFile?: string;
}

export interface EventLayoutSection {
    sectionType: string;
    sectionName: string;
    ga: boolean;
    accessible: boolean;
    image: string | null;
    svg: string | null;
    vcsId: number;
    vsId: number;
    showSeatsRemaining: boolean;
    addableToCart: boolean;
    showSectionNumber: boolean;
    boxofficeOnly: boolean;
    sections?: Array<EventLayoutSection>;
    seats?: Array<EventLayoutSeat>;
}

export interface EventLayoutGeneralSection {
    id: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    relationship: string;
    fill: string;
    displayText: string | null;
    fontSize: number | null;
    fontColor: string | null;
}

export enum EventLayoutShapeType {
    LINE = "LINE",
    CIRCLE = "CIRCLE",
    RECT = "RECT",
    RAW = "RAW",
}

export interface EventLayoutShape {
    id: number;
    type: EventLayoutShapeType;
    offsetX: number;
    offsetY: number;
    endX: number | null;
    endY: number | null;
    radius: number | null;
    width: number | null;
    height: number | null;
    fill: string;
    stroke: string | null;
    strokeWidth: number | null;
    fillOpacity: number | null;
    path: string | null;
}

export interface EventLayoutText {
    id: number;
    displayText: string;
    fontSize: number;
    fontColor: string;
    offsetX: number;
    offsetY: number;
}

export interface EventLayoutImage {
    id: number;
    url: string;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
}

export interface EventLayoutSeat {
    seatId: number;
    seatNumber: number | string;
    boxofficeOnly: boolean;
    accessible: boolean;
    cx: number;
    cy: number;
    r: number;
    fill: string;
}

export interface Department {
    eventTitle: string;
    ticketSummaryId: number;
}

export interface PermissionSummary {
    disable: boolean;
    comp: boolean;
}

export interface SeatDetails {
    seatId: number;
    status: SeatStatus;
    tickets: { [ticketSummaryId: number]: TicketDetails };
}

export interface TicketDetails {
    ticketId: number;
    price: number;
}

export interface SeatmapOrder {
    id: number;
    user?: SimpleUser;
    address: {
        firstName: string;
        lastName: string;
    };
}

export type SeatMode = "PURCHASE" | "COMP" | "DEACTIVATE";

// * New models
export interface SimpleSectionSeat {
    id: number;
    name: string;
    isAccessible: boolean;
    isObstructed: boolean;
    isBoxofficeOnly: boolean;
    seatDetails?: string;
}

export interface SectionSeat extends SimpleSectionSeat {
    idx: number;
    isActive: boolean;
    seatX: number | null;
    seatY: number | null;
    parentSections: ParentSectionType[];
}

export interface EventPackage {
    id: number;
    name: string;
    weight: number | null;
    description: string | null;
    extendedDetails: string | null;
    startDate: number;
    endDate: number;
    ecommerceStartDate?: number | null;
    ecommerceEndDate?: number | null;
    boxofficeStartDate?: number | null;
    boxofficeEndDate?: number | null;
    posStartDate?: number | null;
    posEndDate?: number | null;
    ecommerceVisibility: DepartmentVisibility;
    boxofficeVisibility: DepartmentVisibility;
    posVisibility: DepartmentVisibility;
    priceGroups: EventPackagePriceGroup[];
    category: DepartmentCategory | null;
}

export interface EventPackagePriceGroup {
    id: number;
    name: string;
    price: number;
    isGeneralAdmission: boolean;
    seatIds: number[];
    sectionIds: number[];
    eventSeatmapIds: number[];
}
